<!--
  SuperAdmin: Audit log -- cross-tenant paginated view.

  Wired to GET /api/superadmin/audit-log (superadmin_audit_log.py).
  Supports filters: event_category, distributor_id, after, before.
  Pagination: page / per_page (backend caps per_page at 200).

  All PII handling is server-side; the frontend renders whatever the
  API returns.

  ASCII-only.
-->
<template>
    <div data-testid="sa-audit-log">
        <div class="sa-head">
            <h1 class="sa-page-title">Audit log</h1>
        </div>

        <!-- Filters -->
        <div class="sa-filters" data-testid="sa-audit-filters">
            <select
                v-model="filter.event_category"
                class="sa-select"
                data-testid="sa-audit-filter-category"
                @change="loadPage(1)"
            >
                <option value="">All categories</option>
                <option value="acl">ACL</option>
                <option value="money">Money</option>
                <option value="impersonation">Impersonation</option>
                <option value="override">Override</option>
                <option value="risk_watch">Risk Watch</option>
                <option value="auth">Auth</option>
                <option value="data_export">Data export</option>
            </select>

            <input
                v-model="filter.distributor_id"
                class="sa-input"
                data-testid="sa-audit-filter-distributor"
                placeholder="Distributor ID"
                type="number"
                @change="loadPage(1)"
            />

            <input
                v-model="filter.after"
                class="sa-input"
                data-testid="sa-audit-filter-after"
                placeholder="After (ISO date)"
                type="date"
                @change="loadPage(1)"
            />

            <input
                v-model="filter.before"
                class="sa-input"
                data-testid="sa-audit-filter-before"
                placeholder="Before (ISO date)"
                type="date"
                @change="loadPage(1)"
            />

            <button
                class="sa-btn-clear"
                data-testid="sa-audit-clear-filters"
                type="button"
                @click="clearFilters"
            >
                Clear
            </button>
        </div>

        <!-- Loading -->
        <p
            v-if="loading"
            class="sa-state"
            data-testid="sa-audit-loading"
        >
            Loading...
        </p>

        <!-- Error -->
        <p
            v-else-if="errorMsg"
            class="sa-state sa-state--error"
            data-testid="sa-audit-error"
        >
            {{ errorMsg }}
        </p>

        <!-- Empty -->
        <p
            v-else-if="rows.length === 0"
            class="sa-state"
            data-testid="sa-audit-empty"
        >
            No audit log entries match your filters.
        </p>

        <!-- Table -->
        <template v-else>
            <table class="sa-table" data-testid="sa-audit-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Category</th>
                        <th>Entity</th>
                        <th>Change</th>
                        <th>Actor</th>
                        <th>Distributor</th>
                        <th>Reason</th>
                        <th>Time</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="row.id"
                        class="sa-table__row"
                        data-testid="sa-audit-row"
                    >
                        <td class="sa-td--mono">{{ row.id }}</td>
                        <td>
                            <span
                                class="sa-badge"
                                :class="`sa-badge--${row.event_category || 'none'}`"
                            >{{ row.event_category || "--" }}</span>
                        </td>
                        <td>
                            {{ row.entity_type || "--" }}
                            <span v-if="row.entity_id" class="sa-id">
                                #{{ row.entity_id }}
                            </span>
                        </td>
                        <td>{{ row.change_type || "--" }}</td>
                        <td class="sa-td--mono">
                            {{ row.actor_account_id || "--" }}
                        </td>
                        <td class="sa-td--mono">
                            {{ row.distributor_id || "--" }}
                        </td>
                        <td class="sa-td--reason">
                            {{ row.reason || "" }}
                        </td>
                        <td class="sa-td--time">
                            {{ formatTime(row.created_at) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <div class="sa-pagination" data-testid="sa-audit-pagination">
                <button
                    class="sa-btn-page"
                    data-testid="sa-audit-prev"
                    :disabled="page <= 1"
                    type="button"
                    @click="loadPage(page - 1)"
                >
                    Prev
                </button>
                <span class="sa-pagination__info">
                    Page {{ page }} &mdash; {{ total }} total
                </span>
                <button
                    class="sa-btn-page"
                    data-testid="sa-audit-next"
                    :disabled="page * perPage >= total"
                    type="button"
                    @click="loadPage(page + 1)"
                >
                    Next
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import api from "@/services/api";

function formatTime(iso) {
    if (!iso) return "--";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default {
    name: "SuperAdminAuditLog",
    data() {
        return {
            loading: true,
            errorMsg: "",
            rows: [],
            total: 0,
            page: 1,
            perPage: 50,
            filter: {
                event_category: "",
                distributor_id: "",
                after: "",
                before: "",
            },
        };
    },
    async mounted() {
        await this.loadPage(1);
    },
    methods: {
        formatTime,
        clearFilters() {
            this.filter = {
                event_category: "",
                distributor_id: "",
                after: "",
                before: "",
            };
            this.loadPage(1);
        },
        async loadPage(newPage) {
            this.loading = true;
            this.errorMsg = "";
            this.page = newPage;

            const params = { page: this.page, per_page: this.perPage };
            if (this.filter.event_category) {
                params.event_category = this.filter.event_category;
            }
            if (this.filter.distributor_id) {
                params.distributor_id = Number(this.filter.distributor_id);
            }
            if (this.filter.after) {
                params.after = `${this.filter.after}T00:00:00Z`;
            }
            if (this.filter.before) {
                params.before = `${this.filter.before}T23:59:59Z`;
            }

            try {
                const resp = await api.get("/superadmin/audit-log", { params });
                const data = resp && resp.data ? resp.data : resp;
                this.rows = data.rows || [];
                this.total = data.total || 0;
            } catch (e) {
                this.errorMsg =
                    "Could not load audit log. Please try again.";
                this.rows = [];
                this.total = 0;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #1a202c;
}
.sa-filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 14px;
}
.sa-select,
.sa-input {
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 13px;
    color: #1a202c;
    background: #fff;
}
.sa-input {
    width: 150px;
}
.sa-btn-clear {
    background: #edf2f7;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 5px 12px;
    font-size: 13px;
    cursor: pointer;
    color: #4a5568;
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    font-size: 13px;
}
.sa-table th {
    text-align: left;
    background: #f7fafc;
    color: #2d3748;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
}
.sa-table__row td {
    padding: 7px 10px;
    border-bottom: 1px solid #edf2f7;
    color: #1a202c;
    vertical-align: middle;
}
.sa-table__row:last-child td {
    border-bottom: none;
}
.sa-td--mono {
    font-family: monospace;
    font-size: 12px;
    color: #4a5568;
}
.sa-td--reason {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #4a5568;
}
.sa-td--time {
    white-space: nowrap;
    color: #4a5568;
    font-size: 12px;
}
.sa-id {
    font-size: 11px;
    color: var(--tf-fg-4);
    margin-left: 3px;
}
.sa-badge {
    display: inline-block;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    background: #edf2f7;
    color: #4a5568;
    white-space: nowrap;
}
.sa-badge--acl { background: #bee3f8; color: #2c5282; }
.sa-badge--money { background: #c6f6d5; color: #22543d; }
.sa-badge--impersonation { background: #fed7d7; color: #742a2a; }
.sa-badge--override { background: #fefcbf; color: #744210; }
.sa-badge--risk_watch { background: #fed7d7; color: #742a2a; }
.sa-badge--auth { background: #e9d8fd; color: #44337a; }
.sa-badge--data_export { background: #feebc8; color: #7b341e; }
.sa-pagination {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    font-size: 13px;
}
.sa-btn-page {
    background: #edf2f7;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 5px 12px;
    font-size: 13px;
    cursor: pointer;
    color: #2d3748;
}
.sa-btn-page:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.sa-pagination__info {
    color: #4a5568;
}
</style>
